import { useEffect } from 'react';
import { Switch, useLocation, Route, Redirect } from 'react-router-dom';
import PrivateRoute from 'app/sharedServices/authentication/PrivateRoute';
import { EventFlowProvider, useEventFlow } from 'app/contexts/EventFlowContext';
import EventLanding from './EventLanding';
import EventPersonalDetails from './EventPersonalDetails';
import EventBusinessDetails from './EventBusinessDetails';
import EventReview from './EventReview';
import EventConfirmation from './EventConfirmation';
import EventManagement from './EventManagement';

/* The URL shapes handled by this router:

  Landing:  /events/:agencyName/landing?eventCode={eventId}
  Internal: /events/:agencyName/(details|business-details|review|confirmation)
  Manage:   /events/:eventId/manage/:bookingId

The landing page and the manage page are entry points into the flow, so they
carry the event identifier in the URL (the eventCode query param and the path
respectively). The internal steps that follow the landing page keep the agency
name in the URL for consistency with landing, but not the event id: once
landing has run, the event id lives in session storage under currentEventId,
so those steps read it from there rather than repeating it in every URL. */
const EVENT_STEP_PATH = '/events/:agencyName/:step(details|business-details|review|confirmation)';

const EventRoutes: React.FC = () => {
  const { search } = useLocation();
  // The landing route carries the event identifier in the eventCode query
  // parameter; used only to keep currentEventId in session storage in sync.
  const landingEventId = new URLSearchParams(search).get('eventCode');
  const { setEventId, eventId, hasGivenConsent } = useEventFlow();

  useEffect(() => {
    if (landingEventId) {
      setEventId(landingEventId);
    }
  }, [landingEventId, setEventId]);

  return (
    <Switch>
      {/* Catch session-timeout redirects from PrivateRoute before any other route.
          PrivateRoute constructs the redirect as `${basePath}logout`, which for
          events resolves to /events/:agencyName/logout. Delegate to the top-level
          /logout commonRoute (same pattern as SBA) rather than rendering Logout directly. */}
      <Redirect from="/events/:agencyName/logout" to="/logout" />

      {/* Landing: agency name in the path, event id in the eventCode query param. */}
      <Route
        exact
        path="/events/:agencyName/landing"
        render={({ location }) => {
          const eventId = new URLSearchParams(location.search).get('eventCode');
          return <EventLanding eventId={eventId} />;
        }}
      />

      {/* Management: event id and booking id both in the path. */}
      <Route
        path="/events/:eventId/manage/:bookingId"
        render={({ match }) => {
          const { eventId, bookingId } = match.params;
          return (
            <PrivateRoute
              path={match.url}
              basePath={`/events/${eventId}/`}
              render={() => <EventManagement eventId={eventId} bookingId={bookingId} />}
            />
          );
        }}
      />

      {/* Internal steps: no event id in the path, step discriminated by the segment.
          The event id is read from session storage, where landing stored it. */}
      <Route
        path={EVENT_STEP_PATH}
        render={({ match }) => {
          const { agencyName, step } = match.params as { agencyName: string; step: string };

          // The landing page is the entry point for the event flow: it loads the
          // event details from the backend and records the user's consent. If the
          // user jumps straight to a later step via a URL (without going through
          // landing), send them back to the landing page to start from there,
          // mirroring the behaviour of the service flow.
          //
          // hasGivenConsent comes from the event context. The landing page records
          // consent through useEventFlow().setConsent, which updates both the
          // context state and session storage, so the context value is the source
          // of truth for the redirect guard here.
          if (!hasGivenConsent) {
            const landingSearch = eventId ? `?eventCode=${encodeURIComponent(eventId)}` : '';
            return <Redirect to={`/events/${agencyName}/landing${landingSearch}`} />;
          }

          const basePath = `/events/${agencyName}/`;

          switch (step) {
            case 'details':
              return (
                <PrivateRoute
                  path={match.url}
                  basePath={basePath}
                  render={() => <EventPersonalDetails agencyName={agencyName} />}
                />
              );
            case 'business-details':
              return (
                <PrivateRoute
                  path={match.url}
                  basePath={basePath}
                  render={() => <EventBusinessDetails agencyName={agencyName} />}
                />
              );
            case 'review':
              return (
                <PrivateRoute
                  path={match.url}
                  basePath={basePath}
                  render={() => <EventReview agencyName={agencyName} />}
                />
              );
            case 'confirmation':
              return <PrivateRoute path={match.url} basePath={basePath} render={() => <EventConfirmation />} />;
            default:
              return null;
          }
        }}
      />

      {/* Catch-all: redirect any unrecognised path under /events/* to the 404 route,
          matching the behaviour of ServiceRouter (history.push('/404')). Must be last. */}
      <Redirect to="/404" />
    </Switch>
  );
};

const EventRouter: React.FC = () => {
  return (
    <EventFlowProvider>
      <EventRoutes />
    </EventFlowProvider>
  );
};

export default EventRouter;
